import React from 'react';
import { AlertTriangle, Crown, ShieldOff } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Badge,
} from 'lift-ui';
import { User } from '../../types';
import '../../iam-support.styles.scss';

interface PromoteToSuperadminSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onPromote: (userId: number) => void;
  isSubmitting?: boolean;
}

const PromoteToSuperadminSheet: React.FC<PromoteToSuperadminSheetProps> = ({
  open,
  onOpenChange,
  user,
  onPromote,
  isSubmitting = false,
}) => {
  const isSuperadmin = user?.isSuperadmin || false;
  const isPromoting = !isSuperadmin;

  const handleToggle = () => {
    if (user) {
      onPromote(user.id);
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" className="iam-support-sheet flex flex-col h-full">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {isPromoting ? (
              <>
                <Crown className="h-5 w-5 text-amber-500" />
                Promote to Superadmin
              </>
            ) : (
              <>
                <ShieldOff className="h-5 w-5 text-red-500" />
                Remove Superadmin Status
              </>
            )}
          </SheetTitle>
          <SheetDescription>
            {isPromoting 
              ? 'This action will grant unrestricted access to the entire system.'
              : 'This action will remove superadmin status and restore normal user permissions.'}
          </SheetDescription>
        </SheetHeader>
        <div className="iam-support-dialog-form flex-1 overflow-y-auto">
          {/* User Info */}
          <div className="p-4 bg-gray-50 rounded-lg border mb-4">
            <div className="font-medium text-lg">{user?.name}</div>
            <div className="text-gray-600">{user?.email}</div>
            {user?.permissions && Object.keys(user.permissions).length > 0 && (
              <div className="mt-3">
                <span className="text-sm text-gray-500">Current permissions:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(user.permissions).map(([product, level]) => (
                    <Badge key={product} variant="outline" className="text-xs">
                      {product}: {level}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Warning */}
          <div className={`p-4 border rounded-lg ${isPromoting ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex gap-3">
              <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isPromoting ? 'text-amber-600' : 'text-red-600'}`} />
              <div>
                <h4 className={`font-semibold ${isPromoting ? 'text-amber-800' : 'text-red-800'}`}>
                  {isPromoting ? 'Warning' : 'Important Notice'}
                </h4>
                <ul className={`mt-2 text-sm space-y-1 ${isPromoting ? 'text-amber-700' : 'text-red-700'}`}>
                  {isPromoting ? (
                    <>
                      <li>• Superadmins have full access to everything in LIFT Admin</li>
                      <li>• All existing permissions will be replaced with superadmin access</li>
                      <li>• Only superadmins can promote or demote other superadmins</li>
                      <li>• This action should only be performed by authorized administrators</li>
                    </>
                  ) : (
                    <>
                      <li>• Removing superadmin status will revoke unrestricted system access</li>
                      <li>• The user will need to be assigned specific product permissions</li>
                      <li>• Only superadmins can demote other superadmins</li>
                      <li>• This action should only be performed by authorized administrators</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant={isPromoting ? "default" : "destructive"}
            onClick={handleToggle}
            disabled={isSubmitting}
            className={isPromoting ? "bg-amber-500 hover:bg-amber-600" : ""}
          >
            {isSubmitting 
              ? (isPromoting ? 'Promoting...' : 'Removing...') 
              : (isPromoting ? 'Promote to Superadmin' : 'Remove Superadmin Status')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default PromoteToSuperadminSheet;
